import {cookieUtils} from "@utilities/cookie-utils/cookie-utils";
import {SalToast} from "@utilities/sal-toast/sal-toast";

let defaultOptions = {
    wishlist: {
        class: "wish-list",
        availableClass: "icon--wishlist-empty",
        selectedClass: "icon--wishlist-filled",
        disabledClass: "disabled"
    },
    cart: {
        class: "icon--shop",
        availableClass: "cart-available",
        selectedClass: "cart-added",
        disabledClass: "inactive"
    },
    showToast: true,
    showToastButton: true
};

const QUANTITY_DELIMITER = ':';
export const CART = 'cart';
export const WISHLIST = 'wishlist';

const shopUtilitiesClass = function() {
    this.options = { ...defaultOptions }

    this.init = () => {
        // run on dom loaded
        // Migrate old cookies
        let migratedCart = this.migrateCookie(CART);
        let migratedWishlist = this.migrateCookie(WISHLIST);
        // Also reload the page if we are on the shop page already :-)
        if ((migratedCart || migratedWishlist) && (window.wishlistdata !== undefined || window.cartdata !== undefined)) {
            window.location.reload();
        }

        // clean up invalid items
        if (Array.isArray(window.cart_invalid_items)) {
            window.cart_invalid_items.forEach(id => {
                shopUtilities.remove(CART, id);
            });
        }
        if (Array.isArray(window.wishlist_invalid_items)) {
            window.wishlist_invalid_items.forEach(id => {
                shopUtilities.remove(WISHLIST, id);
            });
        }
    }

    // Replace 'roomdesigner' with 'decorset' and 'sample_.+_' with 'sample_' in cookie
    this.migrateCookie = (type) => {
        if (!window.market) {
            return false;
        }
        let hasChanges = false;
        const key = `${type}_${window.market}`;
        const cookieValues = cookieUtils.decodeCookieValue(cookieUtils.getCookie(key));
        if (Array.isArray(cookieValues)) {
            cookieValues.forEach(value => {
                if (value.startsWith('roomset#')) {
                    cookieUtils.removeCookieValue(key, value);
                    cookieUtils.addCookieValue(key, value.replace(/^roomset#/, 'decorset#'), 0);
                    hasChanges = true;
                }
                else if (value.startsWith('sample_')) {
                    cookieUtils.removeCookieValue(key, value);
                    cookieUtils.addCookieValue(key, value.replace(/^sample_[^#]+/, 'sample'), 0);
                    hasChanges = true;
                }
            });
        }
        return hasChanges;
    },

    this.configure = (options) => {
        this.options = {...this.options, ...options};
    }

    this.registeredItems = [];
    /**
     * Register listeners for the given item and type.
     * @param item
     * @param type
     * @return function function to unregister the listeners
     */
    this.registerListeners = (item, type) => {
        // Prevent double registration
        if (this.registeredItems.includes(item)) {
            return;
        }
        this.registeredItems.push(item);
        item.addEventListener('click', e => {
            const shopButton = e.target.closest(`.${this.options[type]["class"]}`);

            if (shopButton) {
                if (shopButton.classList.contains("inactive")) {
                    return;
                }
                if (window.market) {
                    const pimId = shopButton.dataset.pid;

                    if (shopButton.classList.contains(this.options[type]["availableClass"])) {
                        shopUtilities.add(type, pimId, {
                            id: pimId,
                            title: shopButton.dataset.title + (shopButton.dataset.titlesuffix || ''),
                            subtitle: shopButton.dataset.subtitle,
                            subsubtitle: shopButton.dataset.subsubtitle
                        });
                    }
                    else if (shopButton.classList.contains(this.options[type]["selectedClass"])) {
                        shopUtilities.remove(type, pimId, {
                            id: pimId,
                            title: shopButton.dataset.title + (shopButton.dataset.titlesuffix || ''),
                            subtitle: shopButton.dataset.subtitle,
                            subsubtitle: shopButton.dataset.subsubtitle
                        });
                    }
                } else {
                    new Error("Could not set market")
                }
            }
        });

        // Update class immediately
        if (cookieUtils.isValueInKey(`${type}_${window.market}`, item.dataset.pid)) {
            item.classList.remove(this.options[type]["availableClass"]);
            item.classList.add(this.options[type]["selectedClass"]);
            if (type === CART) {
                item.setAttribute("disabled", "")
            }
        }

        // Add callbacks
        const addedFn = (e) => {
            if (item.dataset.pid === e.detail.id) {
                item.classList.remove(this.options[type]["availableClass"]);
                item.classList.add(this.options[type]["selectedClass"]);
                if (type === CART) {
                    item.setAttribute("disabled", "")
                }
            }
        };
        const removedFn = (e) => {
            if (item.dataset.pid === e.detail.id) {
                item.classList.remove(this.options[type]["selectedClass"]);
                item.classList.add(this.options[type]["availableClass"]);
                if (type === CART) {
                    item.removeAttribute("disabled")
                }
            }
        };

        shopUtilities.onAdd(addedFn, false, type);
        shopUtilities.onRemove(removedFn, false, type);

        return () => {
            shopUtilities.offAdd(addedFn)
            shopUtilities.offRemove(removedFn);
        }
    }

    this.isAdded = function(type, pimId) {
        if (window.market) {
            return cookieUtils.isValueInKey(`${type}_${window.market}`, pimId);
        }

        return false;
    }

    /**
     * Fügt einen Artikel zum Warenkorb oder zur Wunschliste hinzu.
     *
     * @param {string} type - Der Typ der Liste, entweder 'cart' oder 'wishlist'.
     * @param {string} pimId - Die Produkt-ID des hinzuzufügenden Artikels.
     * @param {Object} payload - Die Nutzlast, die zusätzliche Informationen über den Artikel enthält.
     * @param {string} payload.id - Die Produkt-ID.
     * @param {string} payload.title - Der Titel des Artikels.
     * @param {string} payload.subtitle - Der Untertitel des Artikels.
     * @param {string} payload.subsubtitle - Der zusätzliche Untertitel des Artikels.
     */
    this.add = function(type, pimId, payload) {
        // PFLQS-529 Safeguard against adding more than orderMaxItems to the cart
        if (type === CART) {
            const itemCount = cookieUtils.cookieValueLength(`${type}_${window.market}`);
            if (itemCount >= window.wishcart_orderMaxItems) {
                if (!window.toasts) {
                    window.toasts = new SalToast();
                }
                let errorMessage = window.pimLocalization.shop.notAddedToCart;
                errorMessage = errorMessage.replace(/MAX/, window.wishcart_orderMaxItems);
                errorMessage = errorMessage.replace(/COUNT/, itemCount);

                let button = null;
                if (this.options.showToastButton) {
                    button = {
                        label: window.pimLocalization.shop.goToCart,
                        link: window.page_cart,
                        target: '_self',
                        additionalClass: 'button button--primary icon--before icon--shop'
                    }
                }
                window.toasts.createToast(errorMessage, '', window.toasts.types.INFO, button);
                return;
            }
        }

        if (window.market && cookieUtils.addCookieValue(`${type}_${window.market}`, pimId, window.authenticated ? 0 : 30 * cookieUtils.constants.DAYS)) {
            shopUtilities.broadcastChanges(`${type}add`, payload);
        }
    }

    this.remove = function(type, pimId, payload) {
        if (window.market && cookieUtils.removeCookieValue(`${type}_${window.market}`, pimId, window.authenticated ? 0 : 30 * cookieUtils.constants.DAYS)) {
            shopUtilities.broadcastChanges(`${type}remove`, payload);
        }
    }

    this.getItemCount = function(type) {
        if (window.market) {
            return cookieUtils.cookieValueLength(`${type}_${window.market}`);
        }

        return 0;
    }

    this.changeQuantity = function(type, pimId, quantity) {
        if (window.market && cookieUtils.getCookie(`${type}_${window.market}_quantity`) && cookieUtils.removeCookieValue(`${type}_${window.market}_quantity`, pimId + QUANTITY_DELIMITER + shopUtilities.getQuantity(type, pimId), 0)) {
            if (quantity <= 0) {
                shopUtilities.broadcastChanges(`${type}quantity`, {pim_id: pimId, quantity: quantity});
            }
        }

        if (quantity > 0 && window.market && cookieUtils.addCookieValue(`${type}_${window.market}_quantity`, pimId + QUANTITY_DELIMITER + quantity, 0)) {
            shopUtilities.broadcastChanges(`${type}quantity`, {pim_id: pimId, quantity: quantity});
        }
    }

    this.getQuantity = function(type, pimId) {
        if (window.market) {
            const quantities = cookieUtils.decodeCookieValue(cookieUtils.getCookie(`${type}_${window.market}_quantity`))

            if (!Array.isArray(quantities)) {
                return null
            }

            const result = quantities.find(entry => entry.startsWith(pimId));

            return result !== undefined ? parseInt(result.split(QUANTITY_DELIMITER)[1]) : null
        }
    }

    this.broadcastChanges = function(eventname, payload) {
        // Show to user?
        if (eventname === 'cartadd') {
            if (!window.toasts) {
                window.toasts = new SalToast();
            }

            let button = null;
            if (this.options.showToastButton) {
                button = {
                    label: window.pimLocalization.shop.goToCart,
                    link: window.page_cart,
                    target: '_self',
                    additionalClass: 'button button--primary icon--before icon--shop'
                }
            }
            if(typeof window.toastShopEnabled !== 'undefined' && !window.toastShopEnabled) {
                window.toasts.createToast(window.pimLocalization.shop.addedToCart, '', window.toasts.types.ERROR, button, null, null, null, window.jsI18N.toastShopDisabledNotice);
            } else {
                window.toasts.createToast(window.pimLocalization.shop.addedToCart, '', window.toasts.types.INFO, button);
            }
        }

        // Send to JavaScript
        window.dispatchEvent(new CustomEvent(eventname, {
            bubbles: true,
            detail: payload
        }));

        // Send to Backend
        if (window.wishcart_ping && window.PFL_AUTHENTICATED) {
            fetch(window.wishcart_ping)
                .then(response => {
                    if (response.status !== 200) {
                        if (!window.toasts) {
                            window.toasts = new SalToast();
                        }

                        window.toasts.createToast('You seem to be offline', 'We could not synchronize with your user account', window.toasts.types.INFO);
                    }
                })
                .catch(() => {
                    if (!window.toasts) {
                        window.toasts = new SalToast();
                    }

                    window.toasts.createToast('You seem to be offline', 'We could not synchronize with your user account', window.toasts.types.INFO);
                });
        }
    }

    this.onAdd = (callback, executeOnInit = true, type) => {
        if (executeOnInit) {
            callback();
        }

        window.addEventListener(`${type}add`, callback);
    }
    this.offAdd = (callback, type) => {
        window.removeEventListener(`${type}add`, callback);
    }
    /** @deprecated */
    this.added = this.onAdd

    this.onRemove = (callback, executeOnInit = false, type) => {
        if (executeOnInit) {
            callback();
        }

        window.addEventListener(`${type}remove`, callback);
    }
    this.offRemove = (callback, type) => {
        window.removeEventListener(`${type}remove`, callback);
    }
    /** @deprecated */
    this.removed = this.onRemove

    this.onChange = (callback, executeOnInit = true, type) => {
        this.onAdd(callback, executeOnInit, type);
        this.onRemove(callback, false, type);

        window.addEventListener(`${type}deleted`, callback);
    }
    this.offChange = (callback, type) => {
        this.offAdd(callback, type);
        this.offRemove(callback, type);

        window.removeEventListener(`${type}deleted`, callback);
    }
    /** @deprecated */
    this.changed = this.onChange
};


const shopUtilities = new shopUtilitiesClass();
export { shopUtilities }

